/** Dialog for moving a carrier's plan group to a different requested plan group's SBS page. */

import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { type BenefitPlanGroup } from "@platform/btx-plan-designer";
import { Button, Typography } from "@platform/design-system";
import {
  isUnmappedPageId,
  movePlanDestinations,
} from "@platform/mpx-quotes-grid";
import { useState } from "react";

import { getBenefitPlanGroupLabel } from "../utils/benefitPlanGroupLabel";

import type { BenefitPlanTypeEnum } from "@platform/openapi-types";

export interface MovePlanDialogProps {
  open: boolean;
  productType: BenefitPlanTypeEnum;
  coverageType: string;
  /** The requested plan group this carrier's plan is currently responding to. */
  currentBenefitPlanGroupId?: string;
  benefitPlanGroups: BenefitPlanGroup[];
  onConfirm: (targetBenefitPlanGroupId: string) => void;
  onClose: () => void;
}

export const MovePlanDialog = ({
  open,
  productType,
  coverageType,
  currentBenefitPlanGroupId,
  benefitPlanGroups,
  onConfirm,
  onClose,
}: MovePlanDialogProps): React.JSX.Element => {
  const [innerSelectedId, setInnerSelectedId] = useState("");

  // Same predicate the carrier header's "Move Plan Group" gate uses, so the menu item and this
  // list can never disagree about whether the plan has anywhere to go. The current page
  // is kept (and disabled below) to show where the plan sits today.
  const options = movePlanDestinations(
    productType,
    benefitPlanGroups.filter((group) => !isUnmappedPageId(group.id)),
    coverageType,
  );
  const selectableOptions = options.filter(
    (group) => group.id !== currentBenefitPlanGroupId,
  );
  const selectedId =
    innerSelectedId ||
    (selectableOptions.length === 1 ? selectableOptions[0].id : "");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Typography variant="header-small" gutterBottom>
          Move Plan
        </Typography>
        <Typography variant="body-medium" color="textSecondary" sx={{ mb: 2 }}>
          Select the Coverage page to which you would like this plan moved.
        </Typography>
        <Typography variant="body-large" sx={{ mb: 0.5 }}>
          Coverage Page
        </Typography>
        <Select
          fullWidth
          displayEmpty
          size="small"
          value={selectedId}
          inputProps={{ "aria-label": "Coverage Page" }}
          onChange={(event) => setInnerSelectedId(event.target.value)}
          renderValue={(value) => {
            const selectedGroup = options.find((group) => group.id === value);
            if (!selectedGroup) {
              return (
                <Typography component="span" color="textDisabled">
                  Select a Coverage Page
                </Typography>
              );
            }
            return getBenefitPlanGroupLabel(selectedGroup);
          }}
        >
          {options.map((group) => {
            const isCurrent = group.id === currentBenefitPlanGroupId;
            return (
              <MenuItem key={group.id} value={group.id} disabled={isCurrent}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <span>{getBenefitPlanGroupLabel(group)}</span>
                  {isCurrent && (
                    <Chip label="Current" size="small" sx={{ ml: 1 }} />
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="tertiary">
          Cancel
        </Button>
        <Button
          onClick={() => selectedId && onConfirm(selectedId)}
          variant="primary"
          disabled={!selectedId}
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};
